import './TeamSetting.scss';

import * as React from 'react';

import { PlanType, TeamRole } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import { useAtomValue } from 'jotai';

import { Spinner } from '~shared/components/Spinner';
import { toast } from '~shared/components/Toast';
import { useTeams } from '~teacher/hook/team';
import { useChangeTeamSettings } from '~teacher/hook/team/useChangeTeamSettings';

import { selectedTeamIdAtom } from '../atoms';
import { useTeamSubscriptionPlan } from '../TeamMembers/TeamMembersTable';
import { CanInviteSetting } from './CanInviteSetting';
import { CanViewEmailSetting } from './CanSeeEmailSetting';

export function TeamSettings() {
  const selectedTeamId = useAtomValue(selectedTeamIdAtom);

  if (!selectedTeamId) {
    return <Spinner />;
  }

  return <TeamSettingsControls />;
}

function TeamSettingsControls() {
  const selectedTeamId = useAtomValue(selectedTeamIdAtom);
  const teamSubscriptionPlan = useTeamSubscriptionPlan(selectedTeamId);

  const teamSettingsQuery = useTeams({
    select: (teams) => {
      if (!selectedTeamId) {
        return null;
      }

      const team = teams.find((team) => team.id === selectedTeamId);

      return team?.settings;
    },
  });

  const useTeamPermissionsQuery = useTeams({
    select: (teams) => {
      if (!selectedTeamId) {
        return null;
      }

      const team = teams.find((team) => team.id === selectedTeamId);

      return team?.permissions;
    },
  });

  if (
    !(
      !selectedTeamId ||
      !teamSettingsQuery.data ||
      !teamSubscriptionPlan.data ||
      !useTeamPermissionsQuery.data
    )
  ) {
    const { canInvite, canViewEmail } = teamSettingsQuery.data;
    const { canChangeCanInvite, canChangeCanViewEmail } =
      useTeamPermissionsQuery.data;

    const currentPlan = teamSubscriptionPlan.data;

    return (
      <TeamSettingsControlsData
        canInvite={canInvite}
        canViewEmail={canViewEmail}
        canChangeInvite={canChangeCanInvite}
        canChangeCanViewEmail={canChangeCanViewEmail}
        currentPlan={currentPlan}
        selectedTeamId={selectedTeamId}
      />
    );
  }

  return <Spinner />;
}

interface TeamSettingsControlsDataProps {
  canInvite: Array<TeamRole>;
  canViewEmail: Array<TeamRole>;
  canChangeInvite: boolean;
  canChangeCanViewEmail: boolean;
  currentPlan: PlanType;
  selectedTeamId: string;
}

function TeamSettingsControlsData({
  canInvite,
  canViewEmail,
  canChangeInvite,
  canChangeCanViewEmail,
  currentPlan,
  selectedTeamId,
}: TeamSettingsControlsDataProps) {
  const changeTeamSettings = useChangeTeamSettings();

  const handleChangeInvite = (newValue: Array<TeamRole>) => {
    changeTeamSettings.mutate(
      {
        teamId: selectedTeamId,
        canInvite: newValue,
      },
      {
        onError: (error) => {
          toast.critical('Failed to update team settings'); // TODO PLACEHOLDER Do not translate
          if (__DEV__) {
            console.error(error);
          }
        },
      }
    );
  };

  const handleChangeCanViewEmail = (newValue: Array<TeamRole>) => {
    changeTeamSettings.mutate(
      {
        teamId: selectedTeamId,
        canViewEmail: newValue,
      },
      {
        onError: (error) => {
          toast.critical('Failed to update team settings'); // TODO PLACEHOLDER Do not translate
          if (__DEV__) {
            console.error(error);
          }
        },
      }
    );
  };

  return (
    <div className="team-settings col-span-2 row-gap-4">
      <div className="col-span-2">
        <span className="text-display font-display font-700 text-display leading-8">
          {translate('Team Settings')}
        </span>
      </div>

      <CanInviteSetting
        canChangeInvite={canChangeInvite}
        currentPlan={currentPlan}
        canInvite={canInvite}
        setCanInvite={handleChangeInvite}
      />
      <CanViewEmailSetting
        canChangeCanViewEmail={canChangeCanViewEmail}
        currentPlan={currentPlan}
        canViewEmail={canViewEmail}
        setCanViewEmail={handleChangeCanViewEmail}
      />
    </div>
  );
}
